/**
 * sprayWallCardModel — the pure rules behind a wall card of the Spraywall
 * picker (`SprayWallCard`). docs/spec/21j-spraywall-tab-navigation.md,
 * "The wall card — SprayWallCard", "Pure model".
 */
import { toMillis } from "@sendly/shared";

/**
 * The staff state badge of a wall: the `StatusBadge` tone (`status`) and the
 * `spraywall.wall.{key}` label. Precedence deleting > notPublished > hidden >
 * live — a wall that is both hidden and unpublished says "not published",
 * because that is the one that needs the manager to act.
 */
export function wallCardStatus(wall) {
  if (wall?.deleting === true) return { status: "cancelled", key: "deleting" };
  if (!wall?.currentVersionId) return { status: "draft", key: "notPublished" };
  if (wall.active !== true) return { status: "draft", key: "hidden" };
  return { status: "published", key: "live" };
}

/** A hidden or not-yet-published wall draws a dashed rail (members never see one). */
export function wallCardDashed(wall) {
  const { key } = wallCardStatus(wall);
  return key === "hidden" || key === "notPublished";
}

// `date` is the gym-local day of the send, "YYYY-MM-DD". Parsed as a LOCAL
// midnight so the card shows the same calendar day in every time zone.
function dayMillis(date) {
  const m = typeof date === "string" ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(date) : null;
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime() : 0;
}

/**
 * The viewer's ascents grouped per wall: `Map<wallId, { sends, projects,
 * lastDate }>`. `status === "sent"` counts a send, `"project"` a project;
 * `lastDate` is the latest instant (millis) of the ascent's `lastClimbedAt`,
 * `updatedAt` and `date`, or `null` when none of them is usable.
 */
export function summarizeAscentsByWall(ascents) {
  const byWall = new Map();
  for (const a of Array.isArray(ascents) ? ascents : []) {
    if (!a || typeof a.wallId !== "string" || !a.wallId) continue;
    const entry = byWall.get(a.wallId) || { sends: 0, projects: 0, lastDate: null };
    if (a.status === "sent") entry.sends += 1;
    else if (a.status === "project") entry.projects += 1;
    const latest = Math.max(toMillis(a.lastClimbedAt), toMillis(a.updatedAt), dayMillis(a.date));
    if (latest > 0 && (entry.lastDate === null || latest > entry.lastDate)) entry.lastDate = latest;
    byWall.set(a.wallId, entry);
  }
  return byWall;
}

/**
 * The viewer's own attention per wall, from the two gym-wide own-route queries
 * of the picker: `{ reportCounts, reviewCounts }`, plain objects keyed by wall
 * id. Both count only on the wall's live version: the wall page's "Saját
 * utaid" button counts the same rows, so the card never promises a route the
 * author cannot find there (an archived one cannot be fixed any more). Walls
 * not in `walls` are dropped.
 */
export function summarizeOwnAttention(needsReviewDocs, reportedDocs, walls) {
  const liveVersion = new Map();
  for (const w of Array.isArray(walls) ? walls : []) {
    if (w && typeof w.id === "string" && w.id) liveVersion.set(w.id, w.currentVersionId || null);
  }
  const reviewCounts = {};
  for (const p of Array.isArray(needsReviewDocs) ? needsReviewDocs : []) {
    if (!p || p.status !== "needsReview" || !liveVersion.has(p.wallId)) continue;
    const live = liveVersion.get(p.wallId);
    if (!live || p.versionId !== live) continue;
    reviewCounts[p.wallId] = (reviewCounts[p.wallId] || 0) + 1;
  }
  const reportCounts = {};
  for (const p of Array.isArray(reportedDocs) ? reportedDocs : []) {
    if (!p || !liveVersion.has(p.wallId)) continue;
    const live = liveVersion.get(p.wallId);
    if (!live || p.versionId !== live) continue;
    const n = p.openReportCount;
    if (!Number.isFinite(n) || n <= 0) continue;
    reportCounts[p.wallId] = (reportCounts[p.wallId] || 0) + n;
  }
  return { reportCounts, reviewCounts };
}
